/**
 * TuPlanAutonomoV2 — Página de información del plan para AUTÓNOMOS, en estética
 * individual. Data-driven desde el ArcaPlan real del usuario (rentabilidad,
 * comisiones, estilo) — NADA hardcodeado al plan de empleo.
 *
 * Se muestra solo para los planes Indexa de autónomos (ver gating en Dashboard).
 * La composición por ciclo de vida solo aplica al plan "Ciclo de Vida"; el de
 * "100% Acciones" muestra 100% renta variable. Documentos: pendientes (irán a un
 * bucket), de momento no se muestran.
 */
import { yearsToRetirementFromAge } from '../../../utils/monthlyProjection';
import React, { useContext, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Receipt, PieChart, ShieldCheck } from 'lucide-react';

import { ArcaContext } from '../../../context/ArcaContext.context';
import { I18nContext } from '../../../context/translations/TranslationsContext.context';
import useArcaPlanReturns from '../../../hooks/useArcaPlanReturns';
import { MetricCard } from 'components/ui/metric-card';
import { getMyPensionPlanTransfers } from '../../../api/pension-plan-transfers';
import { resolveActiveArcaPlan } from '../../../utils/resolveActiveArcaPlan';
import { ArcaPlan } from '../../../constants/arcaPlans';
import { periodRangeLabel, todayPct } from '../../../utils/transferReturns';
import { periodWithRange } from '../../../components/MiBeneficioV2/projectionLegend';

// Asignación por ciclo de vida (metodología Indexa) — misma tabla que PlanEmpleoV2.
const LIFE_CYCLE_ALLOCATIONS = [
	{ age: 'Menor de 35 años', ageRange: [0, 34], stocks: 80, bonds: 20 },
	{ age: 'Entre 35 y 44 años', ageRange: [35, 44], stocks: 70, bonds: 30 },
	{ age: 'Entre 45 y 54 años', ageRange: [45, 54], stocks: 60, bonds: 40 },
	{ age: 'Entre 55 y 64 años', ageRange: [55, 64], stocks: 50, bonds: 50 },
	{ age: 'Mayor de 65 años', ageRange: [65, 120], stocks: 40, bonds: 60 }
];

const fmtPct = (n: number): string =>
	`${n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: true })}%`;

interface TuPlanAutonomoV2Props {
	currentAge?: number;
}

const TuPlanAutonomoV2: React.FC<TuPlanAutonomoV2Props> = ({ currentAge: knownAge }) => {
	// Sin su edad, 30 años para la orientación por edad: nunca para pedir tramos de ciclo de vida.
	const currentAge = knownAge ?? 30;
	const { token } = useContext(ArcaContext);
	const {
		state: { translate }
	} = useContext(I18nContext);

	const [plan, setPlan] = useState<ArcaPlan | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	// Rentabilidad de cada plan de Arca, de comparsa (`/plan-returns/arca-plans`, chirigota#576).
	// Con su edad, los de ciclo de vida llegan con su tramo de hoy (chirigota#592).
	const arcaPlanReturns = useArcaPlanReturns(token, yearsToRetirementFromAge(knownAge));

	useEffect(() => {
		if (!token) return;
		getMyPensionPlanTransfers(token)
			.then((res) => {
				const transfers = res?.success && res?.data ? res.data : [];
				setPlan(resolveActiveArcaPlan(transfers) ?? null);
			})
			.catch((error) => console.error('Error resolving user plan:', error))
			.finally(() => setIsLoading(false));
	}, [token]);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-20">
				<div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
			</div>
		);
	}

	if (!plan) return null;

	// Comisión mostrada = solo gestión (el depósito no se suma, por ahora, para cuadrar con el onboarding).
	const isLifeCycle = plan.style.toLowerCase().includes('ciclo de vida');
	// La de su plan, de comparsa: `undefined` mientras llega, `null` sin datos (nunca la del código).
	const planReturn =
		arcaPlanReturns === undefined ? undefined : arcaPlanReturns[plan.catalogSlug] ?? null;
	const returnValue =
		planReturn === undefined
			? '…'
			: planReturn === null
				? '—'
				: todayPct(translate, planReturn, fmtPct(planReturn.annualPct));
	const returnPeriod = planReturn
		? translate('tuPlan.returnNet').replace(
				'{period}',
				periodWithRange(
					translate,
					planReturn.periodYears,
					periodRangeLabel(translate, planReturn.period)
				)
			)
		: undefined;
	// Comisión de gestión de comparsa; si no la tiene, la del catálogo del plan.
	const managementFee = planReturn?.managementFeePct ?? plan.commissionMgmt;
	const userBracket = LIFE_CYCLE_ALLOCATIONS.find(
		(a) => currentAge >= a.ageRange[0] && currentAge <= a.ageRange[1]
	);

	return (
		<div className="individual-v2 -mx-4 -mt-4 min-h-screen">
			<div className="max-w-5xl mx-auto space-y-8 p-4 pt-12 lg:p-6 lg:pt-14">
				{/* Header */}
				<motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
					<h1 className="text-3xl lg:text-[40px] font-bold tracking-[-0.03em] font-heading leading-[1.1] text-foreground">
						{translate('tuPlan.title') || 'Tu plan'}
					</h1>
					<p className="text-base text-muted-foreground mt-1">
						{plan.name} · {plan.manager}
					</p>
				</motion.div>

				{/* KPIs */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<MetricCard
						index={0}
						label={translate('tuPlan.return') || 'Rentabilidad'}
						value={returnValue}
						sub={returnPeriod}
						icon={TrendingUp}
					/>
					<MetricCard
						index={1}
						label={translate('tuPlan.ter') || 'Comisión de gestión'}
						value={fmtPct(managementFee)}
						icon={Receipt}
					/>
				</div>

				{/* Costes y comisiones */}
				<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
					<div className="surface-card p-5 sm:p-6 space-y-4">
						<h2 className="font-heading font-semibold text-lg">
							{translate('tuPlan.costsTitle') || 'Costes y comisiones'}
						</h2>
						<div>
							<p className="text-xs text-muted-foreground">
								{translate('tuPlan.management') || 'Comisión de gestión'}
							</p>
							<p className="text-xl font-bold font-heading text-primary">{fmtPct(managementFee)}</p>
						</div>
					</div>
				</motion.div>

				{/* Composición de cartera */}
				<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
					<div className="surface-card p-5 sm:p-6 space-y-4">
						<h2 className="font-heading font-semibold text-lg flex items-center gap-2">
							<PieChart className="w-5 h-5 text-primary" />
							{translate('tuPlan.composition') || 'Composición de la cartera'}
						</h2>

						{isLifeCycle ? (
							<>
								<p className="text-sm text-muted-foreground">
									{translate('tuPlan.lifeCycleDesc') ||
										'La cartera se ajusta automáticamente según tu edad: más renta variable cuando eres joven, más renta fija al acercarte a la jubilación.'}
								</p>
								<div className="divide-y divide-border/60">
									{LIFE_CYCLE_ALLOCATIONS.map((a) => {
										const isYours = a === userBracket;
										return (
											<div
												key={a.age}
												className={`flex items-center justify-between gap-4 py-2.5 px-3 rounded-lg ${
													isYours ? 'bg-primary/5' : ''
												}`}
											>
												<span
													className={`text-sm ${isYours ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}
												>
													{a.age}
													{isYours && (
														<span className="ml-2 text-xs text-primary">
															{translate('tuPlan.yours') || '(tu tramo)'}
														</span>
													)}
												</span>
												<span className="text-sm font-medium text-foreground shrink-0">
													{a.stocks}% {translate('tuPlan.stocks') || 'acciones'} · {a.bonds}%{' '}
													{translate('tuPlan.bonds') || 'bonos'}
												</span>
											</div>
										);
									})}
								</div>
							</>
						) : (
							<p className="text-sm text-muted-foreground">
								{translate('tuPlan.allStocks') ||
									'Cartera 100% renta variable (acciones), para maximizar el crecimiento a largo plazo.'}
							</p>
						)}
					</div>
				</motion.div>

				{/* Entidad / legal */}
				<p className="text-xs text-muted-foreground/80 flex items-center gap-1.5">
					<ShieldCheck className="w-3.5 h-3.5 shrink-0" />
					{translate('tuPlan.registered') || 'Plan registrado en la DGSFP'} ({plan.dgsfpCode}) ·{' '}
					{translate('tuPlan.managedBy') || 'gestionado por'} {plan.manager}
				</p>
			</div>
		</div>
	);
};

export default TuPlanAutonomoV2;
